import { memo, SyntheticEvent, useMemo, useState } from 'react';

import { isEmpty } from 'remeda';
import useSWR from 'swr';

import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { SLOT_SEARCH_DEBOUNCE, SLOT_SEARCH_MIN_LENGTH } from '../constants';
import { getSearchFooter, PaperFooterContext, PaperWithFooter } from '../paperWithFooter/PaperWithFooter';

import css from './SlotPicker.module.css';

import { slotService } from 'v2/api/slotService';
import { ProviderName } from 'v2/constants';
import { useDebounce } from 'v2/hooks/useDebounce';
import { Nullable } from 'v2/types';
import { SlotModel } from 'v2/types/SlotModel';
import { fileSrc, handleHttpError } from 'v2/utils';

export type SlotOption = Pick<SlotModel, 'identifier' | 'title' | 'providerName' | 'isActive'> & {
  iconId: Nullable<string>;
};

interface SlotPickerProps {
  providerName: ProviderName | '';
  gameId: string;
  onChange: (slot: Nullable<SlotOption>) => void;
}

const getOptionKey = (slot: SlotOption) => `${slot.providerName}:${slot.identifier}`;

const SlotIcon = ({ slot, className }: { slot: SlotOption; className: string }) =>
  slot.iconId ? <img className={className} src={fileSrc(slot.iconId)} alt={slot.title} /> : null;

export const SlotPicker = memo<SlotPickerProps>(({ providerName, gameId, onChange }) => {
  const [query, setQuery] = useState('');
  const [pickedSlot, setPickedSlot] = useState<Nullable<SlotOption>>(null);

  const searchValue = useDebounce(query, SLOT_SEARCH_DEBOUNCE).trim();
  const typedValue = query.trim();
  const canSearch = searchValue.length >= SLOT_SEARCH_MIN_LENGTH;

  const { data: searchResult, isLoading: isSearching } = useSWR(
    canSearch ? ['searchSlots', searchValue, providerName] : null,
    slotService.searchSlots,
    { onError: handleHttpError, shouldRetryOnError: false }
  );

  const isPicked = pickedSlot?.identifier === gameId && pickedSlot.providerName === providerName;

  // The game came from the URL: only providerName + gameId are known, the slot is loaded for display.
  // Shares the cache key with the game tooltip in the table.
  const { data: loadedSlot } = useSWR(
    !isEmpty(gameId) && !isEmpty(providerName) && !isPicked ? ['getSlotByIdentifier', providerName, gameId] : null,
    slotService.getSlotByIdentifier,
    { shouldRetryOnError: false }
  );

  const value = useMemo<Nullable<SlotOption>>(() => {
    if (isEmpty(gameId)) return null;
    if (isPicked) return pickedSlot;

    return (
      loadedSlot ?? {
        identifier: gameId,
        title: gameId,
        providerName: providerName as ProviderName,
        isActive: true,
        iconId: null,
      }
    );
  }, [gameId, providerName, isPicked, pickedSlot, loadedSlot]);

  const options = useMemo<SlotOption[]>(() => {
    const found = canSearch && typedValue === searchValue ? (searchResult?.result ?? []) : [];

    return value && !found.some((slot) => getOptionKey(slot) === getOptionKey(value)) ? [value, ...found] : found;
  }, [canSearch, typedValue, searchValue, searchResult, value]);

  const footer = canSearch ? getSearchFooter(searchResult) : null;

  const isLoading = typedValue.length >= SLOT_SEARCH_MIN_LENGTH && (typedValue !== searchValue || isSearching);

  const handleChange = (_: SyntheticEvent, slot: Nullable<SlotOption>) => {
    setPickedSlot(slot);
    onChange(slot);
  };

  const handleInputChange = (_: SyntheticEvent, inputValue: string, reason: AutocompleteInputChangeReason) => {
    // After a selection MUI puts the label into the input; that must not trigger a search.
    setQuery(reason === 'input' ? inputValue : '');
  };

  return (
    <PaperFooterContext.Provider value={footer}>
      <Autocomplete<SlotOption>
        className={css.slotPicker}
        size="small"
        value={value}
        options={options}
        loading={isLoading}
        filterOptions={(items) => items}
        getOptionLabel={(slot) => slot.title}
        getOptionKey={getOptionKey}
        isOptionEqualToValue={(option, selected) => getOptionKey(option) === getOptionKey(selected)}
        onChange={handleChange}
        onInputChange={handleInputChange}
        PaperComponent={PaperWithFooter}
        loadingText="Поиск…"
        noOptionsText={
          typedValue.length < SLOT_SEARCH_MIN_LENGTH
            ? `Введите минимум ${SLOT_SEARCH_MIN_LENGTH} символа`
            : 'Ничего не найдено'
        }
        renderOption={({ key, ...props }, slot) => (
          <li key={key} {...props}>
            <div className={css.slotOption}>
              <SlotIcon slot={slot} className={css.optionIcon} />

              <div className={css.slotOptionText}>
                <span className={css.title}>{slot.title}</span>
                <span className={css.detail}>
                  {slot.providerName} · {slot.identifier}
                  {!slot.isActive && ' · отключена'}
                </span>
              </div>
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Game"
            placeholder="Title or game ID"
            InputProps={{
              ...params.InputProps,
              startAdornment: value && <SlotIcon slot={value} className={css.inputIcon} />,
            }}
          />
        )}
      />
    </PaperFooterContext.Provider>
  );
});

SlotPicker.displayName = 'SlotPicker';
